// file: src/LandlordPages/LandlordSidebarMobile.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";

const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

export default function LandlordSidebarMobile({
  render,
  loginPath = "/landlord/login",
}) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = prev);
    }
  }, [open]);

  const close = () => setOpen(false);

  // Hard logout (no API): clear storages + redirect
  const handleLogout = () => {
    try {
      localStorage.clear();
    } catch {}
    try {
      sessionStorage.clear();
    } catch {}
    close();
    navigate(loginPath, { replace: true });
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center gap-3 bg-white/85 px-3 py-2 backdrop-blur md:hidden">
        <button
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm shadow-sm active:scale-[0.98]"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 text-slate-700"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 7h18M3 12h18M3 17h18" />
          </svg>
          Menu
        </button>
        <div className="text-sm font-semibold text-slate-900">
          Landlord Dashboard
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.button
              type="button"
              aria-label="Close menu"
              className="fixed inset-0 z-50 bg-black/40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
            />

            {/* Panel */}
            <motion.div
              className="fixed inset-y-0 left-0 z-50 w-[19rem] max-w-[85vw] md:hidden"
              initial={{ x: -340 }}
              animate={{ x: 0 }}
              exit={{ x: -340 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
            >
              {/* Drag handle zone */}
              <motion.div
                className="h-[100dvh]" /* dynamic viewport height for iOS */
                drag="x"
                dragDirectionLock
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.05}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -80 || info.velocity.x < -600) close();
                }}
              >
                {/* Safe-area helper */}
                <style>
                  {`
                    .pb-safe { padding-bottom: max(16px, env(safe-area-inset-bottom, 0px)); }
                  `}
                </style>

                {/* Sheet */}
                <div className="relative flex h-[100dvh] w-[19rem] max-w-[85vw] flex-col border-r border-black/10 bg-white shadow-xl">
                  {/* Sheet header */}
                  <div className="flex items-center justify-between border-b border-black/10 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="grid h-9 w-9 place-items-center rounded-xl text-white shadow"
                        style={{
                          background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
                          boxShadow: "0 10px 22px rgba(42,126,250,.25)",
                        }}
                        aria-label="TowPro"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
                          <path
                            d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">
                        Menu
                      </div>
                    </div>

                    <button
                      onClick={close}
                      aria-label="Close menu"
                      className="rounded-lg border border-black/10 p-2 text-slate-700 hover:bg-black/[.035] active:scale-[0.98]"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M6 6l12 12M6 18L18 6" />
                      </svg>
                    </button>
                  </div>

                  {/* Scrollable content with safe bottom padding */}
                  <div className="h-0 flex-1 overflow-y-auto pb-safe">
                    {typeof render === "function" ? (
                      render(close, handleLogout)
                    ) : (
                      <DefaultMenu
                        isActive={isActive}
                        onNavigate={close}
                        onLogout={handleLogout}
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------------- Default menu (shown if no render prop is provided) ---------------- */

function DefaultMenu({ isActive, onNavigate, onLogout }) {
  const itemBase =
    "group relative flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm border";
  const chip = "grid h-8 w-8 place-items-center rounded-xl border bg-white";
  const dot = (active) =>
    `absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full ${
      active ? "bg-slate-900" : "bg-transparent group-hover:bg-black/20"
    }`;

  return (
    <nav className="p-3">
      {/* Dashboard */}
      <Link to="/landlord/dashboard" onClick={onNavigate} className="block">
        <div
          className={`${itemBase} ${
            isActive("/landlord/dashboard")
              ? "border-black/[0.06] bg-white shadow-sm"
              : "border-transparent hover:bg-black/[.035]"
          }`}
          style={{
            boxShadow: isActive("/landlord/dashboard")
              ? "inset 0 1px 0 rgba(255,255,255,.9), 0 10px 22px rgba(0,0,0,.04)"
              : undefined,
          }}
        >
          <div className={dot(isActive("/landlord/dashboard"))} />
          <div
            className={`${chip} ${
              isActive("/landlord/dashboard")
                ? "border-black/[0.08]"
                : "border-black/[0.06]"
            }`}
          >
            {/* Home icon */}
            <svg
              viewBox="0 0 24 24"
              className={`h-4.5 w-4.5 ${
                isActive("/landlord/dashboard")
                  ? "text-slate-900"
                  : "text-slate-700"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 11l9-7 9 7" />
              <path d="M9 22V12h6v10" />
            </svg>
          </div>
          <span
            className={`font-medium ${
              isActive("/landlord/dashboard")
                ? "text-slate-900"
                : "text-slate-700"
            }`}
          >
            Dashboard
          </span>
          {isActive("/landlord/dashboard") && (
            <span className="ml-auto inline-flex items-center rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white">
              Active
            </span>
          )}
        </div>
      </Link>

      {/* All Bookings (NEW) */}
      <Link
        to="/landlord/all-bookings"
        onClick={onNavigate}
        className="mt-1 block"
      >
        <div
          className={`${itemBase} ${
            isActive("/landlord/all-bookings")
              ? "border-black/[0.06] bg-white shadow-sm"
              : "border-transparent hover:bg-black/[.035]"
          }`}
          style={{
            boxShadow: isActive("/landlord/all-bookings")
              ? "inset 0 1px 0 rgba(255,255,255,.9), 0 10px 22px rgba(0,0,0,.04)"
              : undefined,
          }}
        >
          <div className={dot(isActive("/landlord/all-bookings"))} />
          <div
            className={`${chip} ${
              isActive("/landlord/all-bookings")
                ? "border-black/[0.08]"
                : "border-black/[0.06]"
            }`}
          >
            {/* CalendarDays icon */}
            <svg
              viewBox="0 0 24 24"
              className={`h-4.5 w-4.5 ${
                isActive("/landlord/all-bookings")
                  ? "text-slate-900"
                  : "text-slate-700"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M16 3v4M8 3v4M3 11h18" />
            </svg>
          </div>
          <span
            className={`font-medium ${
              isActive("/landlord/all-bookings")
                ? "text-slate-900"
                : "text-slate-700"
            }`}
          >
            All Bookings
          </span>
          {isActive("/landlord/all-bookings") && (
            <span className="ml-auto inline-flex items-center rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-semibold text-white">
              Active
            </span>
          )}
        </div>
      </Link>

      {/* Divider */}
      <div className="my-3 h-px bg-gradient-to-r from-black/10 to-transparent" />

      {/* Logout */}
      <button
        onClick={onLogout}
        className="w-full rounded-2xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white shadow-lg ring-1 ring-rose-500/25 transition hover:shadow-rose-600/30 active:scale-[0.99]"
        style={{ boxShadow: "0 14px 28px rgba(225,29,72,.22)" }}
      >
        Logout
      </button>
    </nav>
  );
}
